import React, { useState } from 'react';
import { toast } from 'sonner';
import { Plus, Trash2 } from 'lucide-react';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription } from '../../ui/dialog';
import { Button } from '../../ui/button';
import { Input } from '../../ui/input';
import { inventoryApi } from './inventoryApi';

export const RecipeDialog = ({ product, supplies, onClose, onSaved }) => {
  const [lines, setLines] = useState(product.recipe.map((l) => ({ inventory_item_id: l.inventory_item_id, quantity: String(l.quantity) })));
  const [saving, setSaving] = useState(false);
  const byId = Object.fromEntries(supplies.map((s) => [s.id, s]));
  const cost = lines.reduce((t, l) => t + (Number(l.quantity) || 0) * (byId[l.inventory_item_id]?.cost_per_unit || 0), 0);

  const update = (i, patch) => setLines((ls) => ls.map((l, idx) => (idx === i ? { ...l, ...patch } : l)));
  const add = () => setLines((ls) => [...ls, { inventory_item_id: supplies[0]?.id || '', quantity: '1' }]);

  const save = async () => {
    const recipe = lines.filter((l) => l.inventory_item_id && Number(l.quantity) > 0)
      .map((l) => ({ inventory_item_id: l.inventory_item_id, quantity: Number(l.quantity) }));
    setSaving(true);
    try {
      await inventoryApi.setRecipe(product.id, recipe);
      toast.success(`Receta guardada: ${product.name}`);
      onSaved();
      onClose();
    } catch (e) {
      toast.error(e.message);
    } finally {
      setSaving(false);
    }
  };

  return (
    <Dialog open onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="bg-gray-900 border-gray-700 text-white max-w-lg" data-testid="recipe-dialog">
        <DialogHeader>
          <DialogTitle>Receta · {product.name}</DialogTitle>
          <DialogDescription className="text-gray-400">Insumos que se descuentan por cada unidad vendida.</DialogDescription>
        </DialogHeader>
        {supplies.length === 0 ? (
          <p className="text-amber-300 text-sm">Primero crea insumos en la pestaña "Insumos".</p>
        ) : (
          <div className="space-y-2">
            {lines.map((l, i) => (
              <div key={i} className="flex gap-2 items-center" data-testid={`recipe-line-${i}`}>
                <select value={l.inventory_item_id} onChange={(e) => update(i, { inventory_item_id: e.target.value })}
                  className="flex-1 h-9 rounded-md border bg-gray-950 border-gray-700 text-white [color-scheme:dark] px-2 text-sm" data-testid={`recipe-line-supply-${i}`}>
                  {supplies.map((s) => <option key={s.id} value={s.id}>{s.name} ({s.unit})</option>)}
                </select>
                <Input type="number" step="any" value={l.quantity} onChange={(e) => update(i, { quantity: e.target.value })}
                  className="w-24 h-9 bg-black/50 border-gray-700 text-white" data-testid={`recipe-line-qty-${i}`} />
                <span className="text-gray-500 text-xs w-10">{byId[l.inventory_item_id]?.unit}</span>
                <Button size="sm" variant="ghost" className="h-9 w-9 p-1 text-red-400" onClick={() => setLines((ls) => ls.filter((_, idx) => idx !== i))} data-testid={`recipe-line-remove-${i}`}>
                  <Trash2 className="h-4 w-4" />
                </Button>
              </div>
            ))}
            <Button size="sm" variant="outline" onClick={add} className="border-emerald-600/50 text-emerald-300" data-testid="recipe-add-line"><Plus className="h-4 w-4 mr-1" /> Agregar insumo</Button>
            <div className="flex justify-between text-sm border-t border-gray-800 pt-2">
              <span className="text-gray-400">Costo de receta: <b className="text-white" data-testid="recipe-dialog-cost">${cost.toFixed(2)}</b></span>
              <span className="text-gray-400">Precio: ${product.price.toFixed(2)} · Margen: <b className="text-emerald-300">${(product.price - cost).toFixed(2)}</b></span>
            </div>
          </div>
        )}
        <DialogFooter>
          <Button variant="outline" onClick={onClose} className="border-gray-600 text-gray-300">Cancelar</Button>
          <Button onClick={save} disabled={saving || supplies.length === 0} className="bg-emerald-600 hover:bg-emerald-700" data-testid="recipe-save">{saving ? 'Guardando...' : 'Guardar receta'}</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};
